import React, { useState, useEffect, useCallback } from 'react';
import { useSupabaseClient, useUser } from '@supabase/auth-helpers-react';
import { useTranslation } from 'react-i18next';
import { Database } from '@/integrations/supabase/types';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { 
    Table, 
    TableBody, 
    TableCell, 
    TableHead, 
    TableHeader, 
    TableRow 
} from "@/components/ui/table";
import { 
    Dialog, 
    DialogContent, 
    DialogHeader, 
    DialogTitle, 
    DialogDescription, 
    DialogFooter, 
    DialogTrigger 
} from "@/components/ui/dialog";
import { PlusCircle, Edit, Trash2, Loader2 } from 'lucide-react';
import { formatCurrency } from '@/utils/currencyUtils';
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Info } from 'lucide-react';

// Type pour un service premium
type PremiumService = Database['public']['Tables']['premium_services']['Row'];
type PremiumServiceInsert = Database['public']['Tables']['premium_services']['Insert'];
type PremiumServiceUpdate = Database['public']['Tables']['premium_services']['Update'];

// Type pour le formulaire (excluant les champs auto-gérés)
type PremiumServiceForm = Omit<PremiumService, 'id' | 'escort_id' | 'created_at' | 'updated_at'>;

const EscortPremiumServices: React.FC = () => {
    const supabase = useSupabaseClient<Database>();
    const user = useUser();
    const { t } = useTranslation(['escort-premium', 'common']);
    const { toast } = useToast();

    const [services, setServices] = useState<PremiumService[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [editingService, setEditingService] = useState<PremiumService | null>(null);
    const [formData, setFormData] = useState<PremiumServiceForm>({ 
        name: '', 
        description: '', 
        price: 0, 
        currency: 'EUR' 
    });

    // --- Fonctions CRUD (à implémenter) ---
    const loadPremiumServices = useCallback(async () => {
        if (!user) return;
        setIsLoading(true);
        setError(null);
        try {
            console.log("Loading premium services for user:", user.id);
            const { data, error: fetchError } = await supabase
                .from('premium_services')
                .select('*')
                .eq('escort_id', user.id)
                .order('name', { ascending: true }); // Trier par nom

            if (fetchError) {
                throw fetchError;
            }

            console.log("Premium services loaded:", data);
            setServices(data || []); 
        } catch (err) {
            console.error("Error loading premium services:", err);
            setError(t('errors.loadError'));
            toast({ 
              title: t('common:error'), 
              description: t('errors.loadError') + `: ${(err instanceof Error && err.message) || 'Unknown error'}`, 
              variant: 'destructive' 
            });
            setServices([]);
        } finally {
            setIsLoading(false);
        }
    }, [supabase, user, t, toast]);

    useEffect(() => {
        loadPremiumServices();
    }, [loadPremiumServices]);

    const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value, type } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'number' ? parseFloat(value) || 0 : value
        }));
    };

    const handleOpenDialog = (service: PremiumService | null = null) => {
        if (service) {
            setEditingService(service);
            setFormData({ 
                name: service.name,
                description: service.description,
                price: service.price,
                currency: service.currency
            });
        } else {
            setEditingService(null);
            setFormData({ name: '', description: '', price: 0, currency: 'EUR' });
        }
        setIsDialogOpen(true);
    };

    const handleSaveService = async () => {
        if (!user || !formData.name || formData.price <= 0) {
             toast({ 
               title: t('common:warning', { ns: 'common' }), 
               description: t('errors.missingFields'), 
               variant: 'default' 
             });
             return;
        }
        setIsSaving(true);
        try {
            let result;
            const serviceData: Partial<PremiumServiceInsert | PremiumServiceUpdate> = {
                name: formData.name,
                description: formData.description,
                price: formData.price,
                currency: formData.currency.toUpperCase() || 'EUR',
            };

            if (editingService) {
                // Update
                console.log("Updating service:", editingService.id, serviceData);
                const { error } = await supabase
                    .from('premium_services')
                    .update(serviceData)
                    .match({ id: editingService.id, escort_id: user.id }); // Sécurité: vérifier escort_id
                if (error) throw error;
                toast({ 
                  title: t('common:success', { ns: 'common' }), 
                  description: t('success.updateSuccess') 
                });
            } else {
                // Insert
                console.log("Adding new service:", serviceData);
                 const insertData: PremiumServiceInsert = {
                     ...(serviceData as PremiumServiceForm), // Cast car on sait que c'est une insertion
                     escort_id: user.id,
                 };
                const { error } = await supabase
                    .from('premium_services')
                    .insert(insertData);
                if (error) throw error;
                toast({ 
                  title: t('common:success', { ns: 'common' }), 
                  description: t('success.addSuccess') 
                });
            }
            setIsDialogOpen(false);
            await loadPremiumServices(); // Recharger la liste
        } catch (err) {
            console.error("Error saving premium service:", err);
            toast({ 
              title: t('common:error'), 
              description: t('errors.saveError') + `: ${(err instanceof Error && err.message) || 'Unknown error'}`, 
              variant: 'destructive' 
            });
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteService = async (serviceId: string) => {
        if (!user || !window.confirm(t('confirmDelete', { name: services.find(s=>s.id === serviceId)?.name || '' }))) return;
        
        // Optimistic UI update (optionnel mais améliore l'UX)
        const originalServices = services;
        setServices(prev => prev.filter(s => s.id !== serviceId));

        try {
            console.log("Deleting service:", serviceId);
            const { error } = await supabase
                .from('premium_services')
                .delete()
                .match({ id: serviceId, escort_id: user.id }); // Sécurité: vérifier escort_id
            
             if (error) {
                 // Revert optimistic update on error
                 setServices(originalServices);
                 throw error;
             }
             toast({ 
               title: t('common:success', { ns: 'common' }), 
               description: t('success.deleteSuccess') 
             });
            // Pas besoin de recharger si l'update optimiste a réussi
            // await loadPremiumServices(); 
        } catch (err) {
             console.error("Error deleting premium service:", err);
            toast({ 
              title: t('common:error'), 
              description: t('errors.deleteError') + `: ${(err instanceof Error && err.message) || 'Unknown error'}`, 
              variant: 'destructive' 
            });
            // S'assurer de restaurer l'état si l'erreur n'a pas été gérée plus haut
            if (services.length !== originalServices.length) {
                 setServices(originalServices);
            }
        }
    };

    // --- Rendu --- 
    if (isLoading) {
        return <div className="p-4 md:p-6 flex justify-center items-center"><Loader2 className="h-8 w-8 animate-spin text-pink-500"/></div>;
    }

    if (error) {
        return (
            <Alert variant="destructive" className="m-4 md:m-6">
                <AlertTitle>{t('common:error')}</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
            </Alert>
        );
    }

    return (
        <div className="p-4 md:p-6 space-y-6">
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-semibold">{t('title')}</h2>
                <Button onClick={() => handleOpenDialog()}> 
                    <PlusCircle className="mr-2 h-4 w-4" />
                    {t('addNew')}
                </Button>
            </div>

            {services.length === 0 ? (
                 <Alert>
                     <Info className="h-4 w-4" />
                    <AlertTitle>{t('noServices.title')}</AlertTitle>
                    <AlertDescription>{t('noServices.description')}</AlertDescription>
                </Alert>
            ) : (
                <Table>
                     <TableHeader>
                        <TableRow>
                            <TableHead>{t('table.header.name')}</TableHead>
                            <TableHead>{t('table.header.description')}</TableHead>
                            <TableHead className="text-right">{t('table.header.price')}</TableHead>
                            <TableHead className="text-right">{t('common:actions')}</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {/* Mapper les services chargés */}
                        {services.map((service) => (
                            <TableRow key={service.id}>
                                <TableCell className="font-medium">{service.name}</TableCell>
                                <TableCell className="text-sm text-gray-600 max-w-xs truncate">{service.description}</TableCell>
                                <TableCell className="text-right font-mono">{formatCurrency(service.price, service.currency)}</TableCell>
                                <TableCell className="text-right">
                                    <Button variant="ghost" size="icon" className="mr-2 hover:bg-blue-100" onClick={() => handleOpenDialog(service)} title={t('editAction')}>
                                        <Edit className="h-4 w-4 text-blue-600" />
                                    </Button>
                                    <Button variant="ghost" size="icon" className="text-red-600 hover:bg-red-100 hover:text-red-700" onClick={() => handleDeleteService(service.id)} title={t('deleteAction')}>
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}

             {/* Dialog pour Ajouter/Modifier */}
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{editingService ? t('editTitle') : t('addTitle')}</DialogTitle>
                        <DialogDescription>{editingService ? t('editDescription') : t('addDescription')}</DialogDescription>
                    </DialogHeader>
                    <div className="grid gap-4 py-4">
                        <div className="grid grid-cols-4 items-center gap-4">
                            <Label htmlFor="name" className="text-right">{t('form.nameLabel')}</Label>
                            <Input id="name" name="name" value={formData.name} onChange={handleFormChange} className="col-span-3" />
                        </div>
                        <div className="grid grid-cols-4 items-center gap-4">
                            <Label htmlFor="description" className="text-right">{t('form.descriptionLabel')}</Label>
                            <Textarea id="description" name="description" value={formData.description} onChange={handleFormChange} className="col-span-3" />
                        </div>
                        <div className="grid grid-cols-4 items-center gap-4">
                            <Label htmlFor="price" className="text-right">{t('form.priceLabel')}</Label>
                            <Input id="price" name="price" type="number" min="0" step="0.01" value={formData.price} onChange={handleFormChange} className="col-span-3" />
                        </div>
                         <div className="grid grid-cols-4 items-center gap-4">
                            <Label htmlFor="currency" className="text-right">{t('form.currencyLabel')}</Label>
                            {/* TODO: Remplacer par un Select si plusieurs devises sont supportées */}
                            <Input id="currency" name="currency" value={formData.currency} onChange={handleFormChange} className="col-span-3" placeholder="EUR" /> 
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsDialogOpen(false)}>{t('common:cancel')}</Button>
                        <Button onClick={handleSaveService} disabled={isSaving}>
                            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            {t('common:save')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default EscortPremiumServices; 